import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import apiClient from "../../utils/apiClient";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import {
  ArrowLeft,
  MessageCircle,
  Phone,
  Loader2,
  Check,
  X,
  RefreshCw,
  QrCode,
  Unlink,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

export default function ConnectChannels() {
  const navigate = useNavigate();
  const [links, setLinks] = useState({ telegram: null, whatsapp: null });
  const [loading, setLoading] = useState(true);
  const [telegramToken, setTelegramToken] = useState(null);
  const [whatsappToken, setWhatsappToken] = useState(null);
  const [generatingTelegram, setGeneratingTelegram] = useState(false);
  const [generatingWhatsapp, setGeneratingWhatsapp] = useState(false);

  useEffect(() => {
    loadLinks();
  }, []);

  const loadLinks = async () => {
    try {
      const res = await apiClient.get("/agent/channels/linked");
      setLinks(res.data);
    } catch (err) {
      toast.error("Failed to load linked channels");
    } finally {
      setLoading(false);
    }
  };

  const generateToken = async (channel) => {
    const setGenerating = channel === "telegram" ? setGeneratingTelegram : setGeneratingWhatsapp;
    const setToken = channel === "telegram" ? setTelegramToken : setWhatsappToken;

    setGenerating(true);
    try {
      const res = await apiClient.post("/agent/channels/link-token", { channel });
      setToken(res.data);
    } catch (err) {
      toast.error("Failed to generate linking code");
    } finally {
      setGenerating(false);
    }
  };

  const revokeChannel = async (channel) => {
    try {
      await apiClient.post("/agent/channels/revoke", { channel });
      toast.success(`${channel.charAt(0).toUpperCase() + channel.slice(1)} disconnected`);
      setLinks((prev) => ({ ...prev, [channel]: null }));
    } catch (err) {
      toast.error("Failed to disconnect channel");
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    return new Date(isoString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-6" data-testid="connect-channels-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/agent/chat")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold">Connect Messaging Apps</h1>
            <p className="text-muted-foreground text-sm">
              Access your Contract Expert from Telegram or WhatsApp
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Telegram Card */}
          <div className="bg-card border border-border rounded-lg p-6" data-testid="telegram-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#0088cc]/10 rounded-full flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-[#0088cc]" />
              </div>
              <div>
                <h2 className="font-semibold">Telegram</h2>
                <p className="text-sm text-muted-foreground">
                  {links.telegram ? "Connected" : "Not connected"}
                </p>
              </div>
            </div>

            {links.telegram ? (
              <div className="space-y-4">
                <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                    <Check className="w-4 h-4" />
                    <span className="font-medium">Connected</span>
                  </div>
                  <div className="mt-2 text-sm text-green-600 dark:text-green-500">
                    <p>@{links.telegram.channel_username || links.telegram.channel_user_id}</p>
                    <p>Linked: {formatDate(links.telegram.linked_at)}</p>
                  </div>
                </div>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="w-full" data-testid="revoke-telegram">
                      <Unlink className="w-4 h-4 mr-2" />
                      Disconnect Telegram
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Disconnect Telegram?</AlertDialogTitle>
                      <AlertDialogDescription>
                        You will not be able to use your Contract Expert from Telegram anymore. You can reconnect anytime.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => revokeChannel("telegram")}>
                        Disconnect
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ) : telegramToken ? (
              <div className="space-y-4">
                <div className="bg-white dark:bg-gray-900 p-4 rounded-lg flex justify-center">
                  <QRCodeSVG
                    value={telegramToken.qr_data}
                    size={180}
                    level="M"
                    includeMargin
                  />
                </div>
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    Scan with your phone camera or Telegram app
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Expires in {Math.floor(telegramToken.expires_in_seconds / 60)} minutes
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => generateToken("telegram")}
                  disabled={generatingTelegram}
                  data-testid="refresh-telegram-qr"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Generate New Code
                </Button>
              </div>
            ) : (
              <Button
                className="w-full"
                onClick={() => generateToken("telegram")}
                disabled={generatingTelegram}
                data-testid="connect-telegram-btn"
              >
                {generatingTelegram ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <QrCode className="w-4 h-4 mr-2" />
                )}
                Generate QR Code
              </Button>
            )}
          </div>

          {/* WhatsApp Card */}
          <div className="bg-card border border-border rounded-lg p-6" data-testid="whatsapp-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#25D366]/10 rounded-full flex items-center justify-center">
                <Phone className="w-6 h-6 text-[#25D366]" />
              </div>
              <div>
                <h2 className="font-semibold">WhatsApp</h2>
                <p className="text-sm text-muted-foreground">
                  {links.whatsapp ? "Connected" : "Not connected"}
                </p>
              </div>
            </div>

            {links.whatsapp ? (
              <div className="space-y-4">
                <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                    <Check className="w-4 h-4" />
                    <span className="font-medium">Connected</span>
                  </div>
                  <div className="mt-2 text-sm text-green-600 dark:text-green-500">
                    <p>{links.whatsapp.display_name || links.whatsapp.channel_user_id}</p>
                    <p>Linked: {formatDate(links.whatsapp.linked_at)}</p>
                  </div>
                </div>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="w-full" data-testid="revoke-whatsapp">
                      <Unlink className="w-4 h-4 mr-2" />
                      Disconnect WhatsApp
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Disconnect WhatsApp?</AlertDialogTitle>
                      <AlertDialogDescription>
                        You will not be able to use your Contract Expert from WhatsApp anymore. You can reconnect anytime.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => revokeChannel("whatsapp")}>
                        Disconnect
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ) : whatsappToken ? (
              <div className="space-y-4">
                <div className="flex justify-center p-4 bg-white rounded-lg">
                  <QRCodeSVG
                    value={whatsappToken.qr_data}
                    size={180}
                    level="M"
                  />
                </div>
                <p className="text-sm text-center text-muted-foreground">
                  Scan with your phone camera or WhatsApp
                </p>
                <p className="text-xs text-center text-muted-foreground">
                  Expires in {Math.floor(whatsappToken.expires_in_seconds / 60)} minutes
                </p>
                <div className="space-y-2 text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
                  <p className="font-medium text-foreground">After scanning:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>WhatsApp will open with a pre-filled message</li>
                    <li>Tap Send to link your account</li>
                    <li>Start asking contract questions!</li>
                  </ol>
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => generateToken("whatsapp")}
                  disabled={generatingWhatsapp}
                  data-testid="regenerate-whatsapp-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${generatingWhatsapp ? "animate-spin" : ""}`} />
                  Generate New Code
                </Button>
              </div>
            ) : (
              <Button
                className="w-full bg-[#25D366] hover:bg-[#128C7E]"
                onClick={() => generateToken("whatsapp")}
                disabled={generatingWhatsapp}
                data-testid="connect-whatsapp-btn"
              >
                {generatingWhatsapp ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <QrCode className="w-4 h-4 mr-2" />
                )}
                Generate QR Code
              </Button>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-8 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h3 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
            How it works
          </h3>
          <ol className="text-sm text-blue-700 dark:text-blue-300 space-y-2 list-decimal list-inside">
            <li>Click Generate QR Code for the messaging app you want to connect</li>
            <li>Scan the QR code with your phone camera or the messaging app</li>
            <li>Your account will be automatically linked to your Contract Expert</li>
            <li>Start asking questions directly from your messaging app!</li>
          </ol>
        </div>
      </div>
    </Layout>
  );
}
